import { z } from 'zod';
import type { Rule } from './types';

// ─── Format regexes ───

const FORMAT_REGEX: Record<string, RegExp> = {
  phone: /^\+?[\d\s\-().]{7,20}$/,
  alpha: /^[a-zA-Z]+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  numeric: /^\d+$/,
};

// ─── Date helpers ───

/** Resolve 'today', 'now', or an ISO date string to YYYY-MM-DD. */
function resolveDateValue(value: string): string {
  if (value === 'today' || value === 'now') {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return value;
}

// ─── Compiler ───

/**
 * Compile an array of validation rules into a single Zod schema.
 * `fieldType` is the question type from the questionnaire (text, number, date, etc.).
 */
export function compileZodForField(fieldType: string, rules: Rule[]): z.ZodTypeAny {
  if (fieldType === 'number') {
    return buildNumberSchema(rules);
  }
  if (fieldType === 'date') {
    return buildDateSchema(rules);
  }
  // Default: treat as string (text, textarea, select, etc.)
  return buildStringSchema(rules);
}

function buildStringSchema(rules: Rule[]): z.ZodTypeAny {
  let schema = z.string();

  for (const rule of rules) {
    switch (rule.kind) {
      case 'string.minLength':
        schema = schema.min(
          rule.value,
          rule.message ?? `Must be at least ${rule.value} characters`,
        );
        break;
      case 'string.maxLength':
        schema = schema.max(rule.value, rule.message ?? `Must be at most ${rule.value} characters`);
        break;
      case 'string.regex':
        schema = schema.regex(
          new RegExp(rule.pattern, rule.flags),
          rule.message ?? 'Invalid format',
        );
        break;
      case 'string.format':
        schema = applyStringFormat(schema, rule.value, rule.message);
        break;
    }
  }

  return schema;
}

function applyStringFormat(schema: z.ZodString, format: string, message?: string): z.ZodString {
  switch (format) {
    case 'email':
      return schema.email(message ?? 'Invalid email address');
    case 'url':
      return schema.url(message ?? 'Invalid URL');
    case 'phone':
      return schema.regex(FORMAT_REGEX.phone, message ?? 'Invalid phone number');
    case 'alpha':
      return schema.regex(FORMAT_REGEX.alpha, message ?? 'Only letters allowed');
    case 'alphanumeric':
      return schema.regex(FORMAT_REGEX.alphanumeric, message ?? 'Only letters and numbers allowed');
    case 'numeric':
      return schema.regex(FORMAT_REGEX.numeric, message ?? 'Only numbers allowed');
    default:
      return schema;
  }
}

function buildNumberSchema(rules: Rule[]): z.ZodTypeAny {
  let schema = z.coerce.number({ message: 'Must be a valid number' });

  for (const rule of rules) {
    switch (rule.kind) {
      case 'number.min':
        schema = schema.min(rule.value, rule.message ?? `Must be at least ${rule.value}`);
        break;
      case 'number.max':
        schema = schema.max(rule.value, rule.message ?? `Must be at most ${rule.value}`);
        break;
      case 'number.int':
        schema = schema.int(rule.message ?? 'Must be a whole number');
        break;
    }
  }

  return schema;
}

function buildDateSchema(rules: Rule[]): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.string();

  for (const rule of rules) {
    if (rule.kind === 'date.min') {
      const limit = resolveDateValue(rule.value);
      const msg = rule.message ?? `Date must be on or after ${limit}`;
      schema = (schema as z.ZodString).refine((val: string) => val >= limit, msg);
    }
    if (rule.kind === 'date.max') {
      const limit = resolveDateValue(rule.value);
      const msg = rule.message ?? `Date must be on or before ${limit}`;
      schema = (schema as z.ZodString).refine((val: string) => val <= limit, msg);
    }
  }

  return schema;
}
